/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from '@web-stories-wp/react';
import styled from 'styled-components';

/**
 * Internal dependencies
 */
import StoryPropTypes from '../../types';
import { materializeLottieDefinition } from './materialize';
import LottiePlayer from './lottiePlayer';
import useLoadLottieFonts from './useLoadLottieFonts';

const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: transparent;
`;

const Fallback = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PreviewImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const RetainedFrame = styled(Fallback)`
  pointer-events: none;
`;

const Placeholder = styled.div`
  min-width: 52px;
  padding: 6px 8px;
  border-radius: ${({ theme }) => theme.borders.radius.small};
  background: ${({ theme }) => theme.colors.opacity.black10};
  color: ${({ theme }) => theme.colors.fg.primary};
  font-family: ${({ theme }) => theme.typography.family.primary};
  font-size: 12px;
  line-height: 16px;
  text-align: center;
`;

const isObject = (value) =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isUnsupportedAssetPath = (value) =>
  typeof value === 'string' && value && !value.startsWith('data:');

const sanitizeAsset = (asset) => {
  if (!isObject(asset)) {
    return asset;
  }

  const sanitizedAsset = { ...asset };

  if (isUnsupportedAssetPath(sanitizedAsset.p)) {
    sanitizedAsset.p = '';
  }

  if (isUnsupportedAssetPath(sanitizedAsset.u)) {
    sanitizedAsset.u = '';
  }

  return sanitizedAsset;
};

const sanitizeAssets = (value) => {
  if (Array.isArray(value)) {
    return value.map(sanitizeAssets);
  }

  if (!isObject(value)) {
    return value;
  }

  return Object.entries(value).reduce(
    (sanitizedValue, [key, childValue]) => ({
      ...sanitizedValue,
      [key]:
        key === 'assets' && Array.isArray(childValue)
          ? childValue.map((asset) => sanitizeAssets(sanitizeAsset(asset)))
          : sanitizeAssets(childValue),
    }),
    {}
  );
};

function LottieFallback({ previewImageUrl, title }) {
  return previewImageUrl ? (
    <PreviewImage src={previewImageUrl} alt={title || ''} />
  ) : (
    <Placeholder>Lottie</Placeholder>
  );
}

LottieFallback.propTypes = {
  previewImageUrl: PropTypes.string,
  title: PropTypes.string,
};

function LiveLottieDisplay({
  element,
  isAnimatable,
  isAnimationPlaying,
  hasRetainedFrame,
  onRetainFrame,
  onPlayerReady,
}) {
  const containerRef = useRef(null);
  const { previewImageUrl, title } = element;
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const [hasRenderError, setHasRenderError] = useState(false);
  const fontsLoaded = useLoadLottieFonts(element.fonts);
  const materializedDefinitionKey = useMemo(
    () =>
      JSON.stringify({
        defaultParameterValues: element.defaultParameterValues,
        definition: element.definition,
        parameterSchema: element.parameterSchema,
        parameterValues: element.parameterValues,
      }),
    [
      element.definition,
      element.defaultParameterValues,
      element.parameterSchema,
      element.parameterValues,
    ]
  );
  const materializationInput = useMemo(
    () => ({
      defaultParameterValues: element.defaultParameterValues,
      definition: element.definition,
      parameterSchema: element.parameterSchema,
      parameterValues: element.parameterValues,
    }),
    [materializedDefinitionKey]
  );
  const materializedDefinition = useMemo(() => {
    try {
      return sanitizeAssets(materializeLottieDefinition(materializationInput));
    } catch {
      return null;
    }
  }, [materializationInput]);

  useEffect(() => {
    setHasRenderError(false);
    setIsPlayerReady(false);
  }, [materializedDefinitionKey]);

  // Capture before passive cleanup destroys the SVG or resets its adapter.
  useLayoutEffect(() => {
    const container = containerRef.current;
    return () => onRetainFrame(container);
  }, [onRetainFrame]);

  const showFallback =
    !fontsLoaded || !materializedDefinition || hasRenderError || !isPlayerReady;

  return (
    <Container
      ref={containerRef}
      data-lottie-status={hasRenderError ? 'error' : undefined}
      style={{ opacity: hasRetainedFrame ? 0 : 1 }}
    >
      {fontsLoaded && materializedDefinition && !hasRenderError && (
        <LottiePlayer
          animationData={materializedDefinition}
          elementId={element.id}
          enableAnimationControls={isAnimatable}
          inspectionMode={!isAnimationPlaying}
          onError={() => setHasRenderError(true)}
          onReady={() => {
            setIsPlayerReady(true);
            onPlayerReady();
          }}
          playOnReady={isAnimationPlaying}
          playback={element.playback}
        />
      )}
      {showFallback && !hasRetainedFrame && (
        <Fallback>
          <LottieFallback previewImageUrl={previewImageUrl} title={title} />
        </Fallback>
      )}
    </Container>
  );
}

LiveLottieDisplay.propTypes = {
  element: StoryPropTypes.elements.lottie.isRequired,
  isAnimatable: PropTypes.bool,
  isAnimationPlaying: PropTypes.bool,
  hasRetainedFrame: PropTypes.bool.isRequired,
  onRetainFrame: PropTypes.func.isRequired,
  onPlayerReady: PropTypes.func.isRequired,
};

function LottieDisplay({
  element,
  isAnimatable,
  isAnimationPlaying,
  previewMode,
}) {
  const { previewImageUrl, title } = element;
  const retainedFrameRef = useRef(null);
  const [hasRetainedFrame, setHasRetainedFrame] = useState(false);
  const retainFrame = useCallback((container) => {
    const player = container?.querySelector('[data-lottie-status="ready"]');
    if (!retainedFrameRef.current || !player?.querySelector('svg')) {
      return;
    }

    // Retain the player's visibility too: Hide and Reset may intentionally blank it.
    const frame = player.cloneNode(true);
    frame.removeAttribute('data-testid');
    retainedFrameRef.current.replaceChildren(frame);
    setHasRetainedFrame(true);
  }, []);
  const clearRetainedFrame = useCallback(() => {
    retainedFrameRef.current?.replaceChildren();
    setHasRetainedFrame(false);
  }, []);

  if (previewMode) {
    return (
      <Container>
        <LottieFallback previewImageUrl={previewImageUrl} title={title} />
      </Container>
    );
  }

  // Isolate adapters so a queued playback reset cannot seek the inspection player.
  return (
    <Container>
      <LiveLottieDisplay
        key={isAnimationPlaying ? 'playback' : 'inspection'}
        element={element}
        isAnimatable={isAnimatable}
        isAnimationPlaying={isAnimationPlaying}
        hasRetainedFrame={hasRetainedFrame}
        onRetainFrame={retainFrame}
        onPlayerReady={clearRetainedFrame}
      />
      <RetainedFrame
        ref={retainedFrameRef}
        data-lottie-retained-frame
        aria-hidden
      />
    </Container>
  );
}

LottieDisplay.propTypes = {
  element: StoryPropTypes.elements.lottie.isRequired,
  isAnimatable: PropTypes.bool,
  isAnimationPlaying: PropTypes.bool,
  previewMode: PropTypes.bool,
};

export default LottieDisplay;
